import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay, Navigation, Pagination } from 'swiper'
import { Box, IconButton, useMediaQuery, useTheme } from '@material-ui/core'
import { ArrowBackIos, ArrowForwardIos } from '@material-ui/icons'
import NewsItemCard from 'src/components/NewsItemCard/NewsItemCard'
import NewsCardSkeleton from 'src/components/Skeleton/News/NewsCardSkeleton/NewsCardSkeleton'
import getCategoryRouteByCategoryId from 'src/utils/News/getCategoryRouteByCategoryId'
import { useStyles } from './NewsCarousel.styles'

import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
import 'swiper/components/pagination/pagination.min.css'

SwiperCore.use([Navigation, Pagination, Autoplay])

type NewsCarouselProps = {
  items: any[]
  categories: any[]
  isLoading: boolean
}

const NewsCarousel = ({ items, categories, isLoading }: NewsCarouselProps) => {
  const { t } = useTranslation()
  const [swiperInstance, setSwiperInstance] = useState<any>(null)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'))
  const skeletonCount = isDesktop ? 3 : isTablet ? 2 : 1

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setInit] = useState(false)

  const classes = useStyles()

  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.update()
      setInit((prev) => !prev)
    }
  }, [items, swiperInstance])

  return (
    <Box className={classes.noticeCarousel}>
      <IconButton
        aria-label={t('arial-label.previous-slide')}
        className={classes.navButton}
        onClick={() => swiperInstance?.slidePrev()}
        disabled={swiperInstance?.isBeginning}
      >
        <ArrowBackIos style={{ marginLeft: '4px' }} />
      </IconButton>
      <Swiper
        observer={true}
        observeParents={true}
        slidesPerView={1}
        centerInsufficientSlides={true}
        watchOverflow={false}
        threshold={5}
        className={classes.swiperContainer}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper)
          setInit(true)
        }}
        onInit={(swiper) => {
          setSwiperInstance(swiper)
        }}
        onSlideChange={() => setInit((prev) => !prev)}
        onTransitionEnd={() => setInit((prev) => !prev)}
        onActiveIndexChange={() => setInit((prev) => !prev)}
        pagination={{ clickable: true }}
        breakpoints={{
          600: { slidesPerView: 2 },
          1280: { slidesPerView: 3 }
        }}
      >
        {!isLoading
          ? items.map((data: any) => (
              <SwiperSlide key={data.id}>
                <NewsItemCard
                  idNews={data.id}
                  title={data.title}
                  image={data.path}
                  alt={data.alt}
                  text={data.text}
                  category={getCategoryRouteByCategoryId(
                    data.newsCategoriesId,
                    categories
                  )}
                />
              </SwiperSlide>
            ))
          : [...Array(skeletonCount)].map((_, i) => (
              <SwiperSlide key={i}>
                <NewsCardSkeleton />
              </SwiperSlide>
            ))}
      </Swiper>
      <IconButton
        aria-label={t('arial-label.next-slide')}
        className={classes.navButton}
        onClick={() => swiperInstance?.slideNext()}
        disabled={swiperInstance?.isEnd}
      >
        <ArrowForwardIos />
      </IconButton>
    </Box>
  )
}

export default NewsCarousel
